import { Component, h, Prop, Element } from '@stencil/core';
import { RecordType } from '../aha-record-reference/types';

const RECORD_ICONS: Record<RecordType, string> = {
  'Epic': 'aha-icon aha-icon-epic-solid',
  'Feature': 'fa-solid fa-grid-2 fa-fw',
  'Idea': 'fa-solid fa-lightbulb', // NOTE: the class get stored as just Idea in case of integration trigger
  'Ideas::Idea': 'fa-solid fa-lightbulb',
  'Ideas::IdeaUser': 'fa-solid fa-address-book fa-fw',
  'Ideas::IdeaOrganization': 'fa-solid fa-building fa-fw',
  'Ideas::IdeaTheme': 'fa-solid fa-circle-nodes',
  'Initiative': 'fa-solid fa-sitemap',
  'Iteration': 'fa-solid fa-input-pipe',
  'MasterFeature': 'aha-icon aha-icon-epic-solid',
  'MasterRelease': 'fa-solid fa-calendar-lines',
  'Page': 'fa-solid fa-file-lines',
  'Release': 'fa-solid fa-calendar-lines',
  'ReleasePhase': 'fa-solid fa-input-pipe fa-fw',
  'Requirement': 'fa-solid fa-list',
  'StrategicImperative': 'fa-solid fa-flag',
  'Task': 'aha-icon aha-icon-complete',
  'Project': 'fa-regular fa-circle-info',
  'Discovery::Study': 'fa-regular fa-user-magnifying-glass',
  'Study': 'fa-regular fa-user-magnifying-glass',
  'Discovery::Interview': 'fa-regular fa-comments',
  'Interview': 'fa-regular fa-comments',
  'Discovery::File': 'fa-regular fa-file',
  'File': 'fa-regular fa-file',
  'Report': 'aha-icon aha-icon-roadmap',
  'Competitor': 'fa-solid fa-trophy',
  'Persona': 'fa-regular fa-address-card',
  'Polls::Poll': 'fa-solid fa-chart-simple',
  'BusinessModel': 'aha-icon aha-icon-strategic-model-solid fa-fw',
};

const RECORD_PREFIXES: Record<string, RecordType> = {
  E: 'Epic',
  S: 'Initiative',
  N: 'Page',
  R: 'Release',
  G: 'StrategicImperative',
  I: 'Idea',
  IN: 'Discovery::Interview',
  FI: 'Discovery::File',
  ST: 'Discovery::Study',
  SP: 'Iteration',
  T: 'Ideas::IdeaTheme',
  P: 'Persona',
  C: 'Competitor',
  POLL: 'Polls::Poll',
  O: 'Ideas::IdeaOrganization',
  M: 'BusinessModel',
};

export interface AhaRecordIcon extends HTMLAhaRecordIconElement {}

/**
 * Renders an icon for an Aha! record based on record type or reference number.
 */
@Component({
  tag: 'aha-record-icon',
  styleUrl: 'aha-record-icon.css',
  shadow: true,
})
export class AhaRecordIcon {
  @Element() host!: HTMLAhaRecordIconElement;

  /**
   * Type of record represented by the icon.
   */
  @Prop() recordType?: RecordType;

  /**
   * Reference number to auto-detect record type from (e.g., "DEMO-E-1234").
   */
  @Prop() referenceNum?: string;

  /**
   * Optional icon override. When provided, this icon will be used instead of the default record type icon.
   */
  @Prop() iconOverride?: string;

  // Auto-detect the record type from the reference number if possible.
  detectRecordType(): RecordType {
    if (this.recordType) {
      return this.recordType;
    }

    if (!this.referenceNum || this.referenceNum.trim().length === 0) {
      return 'Feature';
    }

    if (this.referenceNum.match(/Report-\d+/)) {
      return 'Report';
    }

    // Prefixes like DEMO-E-1234
    const REFERENCE_REGEX = /^\w+-(([A-Z]+)-)?\d+$/;
    const match = this.referenceNum.match(REFERENCE_REGEX);
    if (!match) {
      return 'Feature';
    }
    const prefix = match[2];
    if (prefix && prefix in RECORD_PREFIXES) {
      return RECORD_PREFIXES[prefix as keyof typeof RECORD_PREFIXES];
    }
    // Numberic prefixes like DEMO-1234-2
    const numericMatch = this.referenceNum.match(/\w+-\d+-\d+$/);
    if (numericMatch) {
      return 'Requirement';
    }

    return 'Feature';
  }

  render() {
    const recordType = this.detectRecordType();
    const recordIcon = this.iconOverride || RECORD_ICONS[recordType];

    return (
      <div data-record-type={recordType}>
        <aha-icon icon={recordIcon} />
      </div>
    );
  }
}
